import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Lock } from 'lucide-react';
import { LISANS_PASIF_OLAYI } from '@lib/api';
import { useAuthStore } from '@store/auth';
import { bitisTarihiMetni, saltOkunurModda, VERI_DISA_AKTAR_IZNI } from './lisansPasif';
import { useVeriIndir } from './useVeriIndir';

const BILDIRIM_SURESI_MS = 5000;

/**
 * Lisans pasif modda üstte sabit bant + 403 bildirimi.
 * Bant: amber dolgu, koyu yazı (warning-metin, AAA). Düğme yalnız indirme
 * yetkisi olana görünür. Bildirim: değiştiren istek LISANS_PASIF ile
 * reddedilince kısa süre altta çıkar ve profil (me) yenilenir.
 */
export function LisansPasifBandi() {
  const me = useAuthStore((s) => s.me);
  const hasIzin = useAuthStore((s) => s.hasIzin);
  const queryClient = useQueryClient();
  const { indir, indiriliyor, hata } = useVeriIndir();
  const [bildirim, setBildirim] = useState(false);

  useEffect(() => {
    let zamanlayici: ReturnType<typeof setTimeout> | undefined;
    const goster = () => {
      setBildirim(true);
      clearTimeout(zamanlayici);
      zamanlayici = setTimeout(() => setBildirim(false), BILDIRIM_SURESI_MS);
      // Bant henüz görünmüyorsa (eski profil) güncel durumu çek.
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    };
    window.addEventListener(LISANS_PASIF_OLAYI, goster);
    return () => {
      window.removeEventListener(LISANS_PASIF_OLAYI, goster);
      clearTimeout(zamanlayici);
    };
  }, [queryClient]);

  const pasif = saltOkunurModda(me?.saltOkunurBitis);
  const indirebilir = hasIzin(VERI_DISA_AKTAR_IZNI);

  return (
    <>
      {pasif && me?.saltOkunurBitis && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-warning px-4 py-2.5 text-sm text-warning-metin md:px-6"
        >
          <p className="flex min-w-0 flex-1 items-center gap-2 font-medium">
            <Lock className="h-4 w-4 shrink-0" aria-hidden />
            <span>
              {indirebilir
                ? `Lisansınız pasif. ${bitisTarihiMetni(me.saltOkunurBitis)} tarihine kadar verilerinizi indirebilirsiniz.`
                : `Lisansınız pasif. Hesabınız ${bitisTarihiMetni(me.saltOkunurBitis)} tarihine kadar yalnız görüntüleme için açık.`}
            </span>
          </p>
          {indirebilir && (
            <button
              type="button"
              onClick={() => void indir()}
              disabled={indiriliyor}
              className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md bg-warning-metin px-4 font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning-metin focus-visible:ring-offset-2 focus-visible:ring-offset-warning disabled:opacity-60"
            >
              <Download className="h-4 w-4" aria-hidden />
              {indiriliyor ? 'Hazırlanıyor…' : 'Verilerimi indir'}
            </button>
          )}
          {hata && <p className="w-full text-sm font-medium">{hata}</p>}
        </div>
      )}
      {bildirim && (
        <div
          role="alert"
          className="fixed inset-x-4 bottom-20 z-50 mx-auto max-w-md rounded-lg bg-foreground px-4 py-3 text-sm text-background shadow-lg md:bottom-6"
        >
          Lisansınız pasif. Hesabınız yalnız görüntüleme ve veri indirme için açık.
        </div>
      )}
    </>
  );
}
